import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useCustomKeycloak } from './CustomKeycloakAuth';

const AuthContext = createContext({
  user: null,
  loading: true,
  error: null,
  isAuthenticated: false,
  sessionExpired: false,
  handleSessionExpired: () => {},
  login: () => {},
  logout: () => {},
  register: () => {},
  hasRole: () => false,
  hasGroup: () => false,
  getToken: () => null,
  refreshToken: () => Promise.resolve(null),
  keycloak: null,
});

// Storage key used by the bypass provider to remember that the user has
// manually signed out. We persist it so the flag survives a full page reload
// (without it, the bypass provider would auto-authenticate again on init).
const BYPASS_SIGNED_OUT_KEY = 'dt-bypass-signed-out';

// Read once at module load so the initial state reflects any prior sign-out.
const readBypassSignedOut = () => {
  try {
    return sessionStorage.getItem(BYPASS_SIGNED_OUT_KEY) === '1';
  } catch (_) {
    return false;
  }
};

// Bypass Auth Provider for development mode
export const BypassAuthProvider = ({ children }) => {
  const [loading] = useState(false);
  const initiallySignedOut = readBypassSignedOut();

  const [user, setUser] = useState(
    initiallySignedOut
      ? null
      : {
          id: 'dev-user',
          email: 'developer@dthealthai.ca',
          name: 'Development User',
          firstName: 'Dev',
          lastName: 'User',
          roles: ['admin'],
          groups: ['developers'],
        }
  );
  const [isAuthenticated, setIsAuthenticated] = useState(!initiallySignedOut);

  // Local sign-out for the bypass path. Persists a "signed out" flag in
  // sessionStorage so the provider doesn't auto-authenticate the user again
  // after the redirect / reload, then hard-navigates to the public homepage.
  const logout = async () => {
    try {
      sessionStorage.setItem(BYPASS_SIGNED_OUT_KEY, '1');
    } catch (_) { /* storage may be locked down — sign-out will still work in-memory */ }

    setUser(null);
    setIsAuthenticated(false);

    try {
      localStorage.removeItem('kc-callback');
      sessionStorage.removeItem('kc-callback');
    } catch (_) { /* ignore */ }

    // Hard navigation so in-memory caches / contexts reset cleanly. We use
    // replace() so Back doesn't bring the user straight back into the app.
    window.location.replace(window.location.origin + '/');
  };

  // Re-authentication (the "Sign In" button on the public homepage). Clears
  // the persisted sign-out flag and restores the dev user.
  const login = () => {
    try {
      sessionStorage.removeItem(BYPASS_SIGNED_OUT_KEY);
    } catch (_) { /* ignore */ }

    setUser({
      id: 'dev-user',
      email: 'developer@dthealthai.ca',
      name: 'Development User',
      firstName: 'Dev',
      lastName: 'User',
      roles: ['admin'],
      groups: ['developers'],
    });
    setIsAuthenticated(true);
    // Land the dev user back on the dashboard like the real flow would.
    window.location.replace(window.location.origin + '/dashboard');
  };

  const value = {
    user,
    loading,
    error: null,
    isAuthenticated,
    sessionExpired: false,
    handleSessionExpired: () => console.log('Session expiration bypassed in development'),
    login,
    logout,
    register: () => console.log('Register bypassed in development'),
    hasRole: () => true,
    hasGroup: () => true,
    getToken: () => (isAuthenticated ? 'dev-token' : null),
    refreshToken: () => Promise.resolve(isAuthenticated ? 'dev-token' : null),
    keycloak: null,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider = ({ children }) => {
  const customKeycloak = useCustomKeycloak();

  // Pass through the custom Keycloak context values
  // Pass through the custom Keycloak context values
  const value = useMemo(() => ({
    user: customKeycloak.user,
    loading: customKeycloak.loading,
    error: customKeycloak.error,
    isAuthenticated: customKeycloak.isAuthenticated,
    sessionExpired: customKeycloak.sessionExpired,
    handleSessionExpired: customKeycloak.handleSessionExpired,
    login: customKeycloak.login,
    logout: customKeycloak.logout,
    register: customKeycloak.register,
    hasRole: customKeycloak.hasRole,
    hasGroup: customKeycloak.hasGroup,
    getToken: customKeycloak.getToken,
    refreshToken: customKeycloak.refreshToken,
    keycloak: customKeycloak.keycloak,
  }), [customKeycloak]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
